import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Phone, Search, Eye, CheckCircle2, ArrowRightCircle, Inbox } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const ENQUIRY_LABELS = {
  care: 'Care',
  staffing: 'Staffing',
  platform_demo: 'CareOps demo',
  recruitment: 'Recruitment',
  other: 'Other',
};

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700',
  spam: 'bg-gray-100 text-gray-500',
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function ContactSubmissionsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('new');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const fetchRows = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/contact-submissions`;
      const params = [];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (typeFilter !== 'all') params.push(`enquiry_type=${typeFilter}`);
      if (params.length) url += `?${params.join('&')}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRows(data.contact_submissions || []);
      } else {
        toast.error('Failed to load contact submissions');
      }
    } catch {
      toast.error('Failed to load contact submissions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    setLoading(true);
    fetchRows();
  }, [fetchRows]);

  const openDetail = (row) => {
    setSelected(row);
    setAdminNotes(row.admin_notes || '');
  };

  const promoteToSalesCrm = async () => {
    if (!selected) return;
    setPromoting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/contact-submissions/${selected.id}/create-sales-lead`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.already_linked ? 'Already in Sales CRM' : `Lead created: ${data.sales_lead.lead_code}`);
        fetchRows();
        if (data.sales_lead?.id) navigate('/portal/sales-leads');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create lead');
      }
    } catch {
      toast.error('Failed to create lead');
    } finally {
      setPromoting(false);
    }
  };

  const patchSubmission = async (status) => {
    if (!selected) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/contact-submissions/${selected.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, admin_notes: adminNotes.trim() || undefined }),
      });
      if (res.ok) {
        toast.success('Updated');
        setSelected(null);
        fetchRows();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const filtered = rows.filter((row) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (row.full_name || '').toLowerCase().includes(q)
      || (row.email || '').toLowerCase().includes(q)
      || (row.organisation || '').toLowerCase().includes(q)
      || (row.subject || '').toLowerCase().includes(q)
      || (row.message || '').toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contact-submissions-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Website enquiries</h1>
          <p className="text-sm text-text-muted mt-1">
            Messages from the contact form — replies to info@osabeacares.co.uk land in Portal Inbox → General
          </p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/portal/inbox?department=info">
            <Inbox className="h-4 w-4 mr-2" />
            General Inbox
          </Link>
        </Button>
      </div>

      <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 text-sm text-blue-900">
        Staffing enquiries should be logged in{' '}
        <Link to="/portal/sales-leads" className="font-medium underline">Sales CRM</Link>
        {' '}then converted to a{' '}
        <Link to="/portal/staffing-requests" className="font-medium underline">Staffing Request</Link>.
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Search name, email, organisation…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="in_progress">In progress</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
        >
          <option value="all">All types</option>
          <option value="staffing">Staffing</option>
          <option value="platform_demo">CareOps demo</option>
          <option value="care">Care</option>
          <option value="recruitment">Recruitment</option>
          <option value="other">Other</option>
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Mail className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-text-muted">No contact submissions match your filters.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Received</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">From</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Subject</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-text-muted">{fmtDate(row.created_at)}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{row.full_name}</p>
                    <p className="text-xs text-text-muted">{row.organisation || row.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      row.enquiry_type === 'platform_demo'
                        ? 'bg-emerald-100 text-emerald-700'
                        : row.enquiry_type === 'staffing'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-gray-100 text-gray-600'
                    }`}>
                      {ENQUIRY_LABELS[row.enquiry_type] || row.enquiry_type || 'Other'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-[200px] truncate">{row.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[row.status] || STATUS_COLORS.new}`}>
                      {row.status || 'new'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openDetail(row)}>
                      <Eye className="h-3 w-3 mr-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selected?.subject}</DialogTitle>
            <DialogDescription>
              {selected?.full_name} · {fmtDate(selected?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm">
                {selected.phone && (
                  <a href={`tel:${selected.phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                    <Phone className="h-3 w-3" />{selected.phone}
                  </a>
                )}
                <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                  <Mail className="h-3 w-3" />{selected.email}
                </a>
              </div>
              {selected.organisation && (
                <p className="text-sm"><span className="text-text-muted">Organisation:</span> {selected.organisation}</p>
              )}
              <p className="text-sm whitespace-pre-wrap border rounded-lg p-3 bg-gray-50">{selected.message}</p>
              <div className="space-y-2">
                <Label>Admin notes</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={3}
                  placeholder="Callback notes, next steps…"
                />
              </div>
              <div className="flex flex-wrap gap-2 justify-between">
                <Button variant="secondary" onClick={promoteToSalesCrm} disabled={promoting || saving}>
                  <ArrowRightCircle className="h-4 w-4 mr-1" />
                  {promoting ? 'Creating…' : 'Add to Sales CRM'}
                </Button>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => patchSubmission('in_progress')} disabled={saving}>
                    Mark in progress
                  </Button>
                  <Button onClick={() => patchSubmission('resolved')} disabled={saving}>
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Resolved
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
